import {
  loadPipelines,
  loadSqlAutomationsPipelines,
  refreshPipeline,
  resetPageNumbers,
  setContextualSupport,
  setCurrentPage,
  setDetailViewCard,
  setSortCriteria,
  resetSortCriteria,
  setTotalItems,
  setTotalPages,
  unsetContextualSupport,
  updateSearchFilters,
  getProjectById,
} from 'js/actions';
import {
  CLONE_FLOW_REQUEST,
  DELETE_PIPELINE_REQUEST,
  DELETE_PROJECTS_REQUEST,
  GET_FLOWS_SQL_AUTOMATIONS_REQUEST,
  GET_PIPELINES_REQUEST,
  REFRESH_PIPELINE_REQUEST,
  REMOVE_FLOW_FROM_PROJECT_REQUEST,
  GET_PROJECT_REQUEST,
} from 'js/constants/actionTypes';
import { ROUTE_PIPELINES, ROUTE_REDESIGN } from 'js/constants/pathnames';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { has } from 'lodash';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { ModalReingest } from 'new/elements/ModalReingest';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import Pipelines from './Pipelines';
import { saveState } from 'new/state/hashedState/action';
import {
  memoizedPipelinesSelector,
  sqlAutomationsPipelinesSelector,
} from 'js/reducers/entities/selectors';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import { ModalAddToMarketplace } from 'new/elements/ModalAddToMarketplace/ModalAddToMarketplace';
import { getCredentialsGroups } from 'js/actions/credentialsGroups';
import { setCurrentProjectId } from 'js/actions/currentProject';

/**
 * Shared state for Pipelines list (main, project, and SQL Automations).
 * @param {Object} state
 * @param {{ sqlAutomations?: boolean, projectId?: string }} options
 */
function getPipelinesState(state, options = {}) {
  const { sqlAutomations = false, projectId: projectIdParam = null } = options;
  const {
    projects: { byIds: projectDataFlowMapping, selectedFlow: projectSelectedFlow, projectById },
    pipelines: pipelinesSlice,
    app: {
      activeViews,
      accessControl,
      visibilityFilters: { filters, availableFilters } = {},
      listing: { currentPage: listingCurrentPage, sortCriteria },
    },
    entities,
    sources: { lastAddedId: lastAddedSourceId },
    dataSets: { lastAddedId: lastAddedDataSetId },
    sinks: { lastAddedId: lastAddedSinkId },
    dataSetSchemas,
    request,
    routes,
  } = state;

  const lastAddedSource = state.entities.sources?.[lastAddedSourceId];
  const getSourceMeta = (flowNodeId) => {
    const {
      entities: { sources, vendors, flowNodes },
      paging: { metaInfoById },
    } = state;
    const sourceId = flowNodes[flowNodeId]?.data_source_id;
    const connectionOrVendorType = getConnectionOrVendorType(
      sources[sourceId],
      vendors,
      'source_type',
      'vendor_id',
    );
    if (connectionOrVendorType && has(metaInfoById, connectionOrVendorType)) {
      return metaInfoById[connectionOrVendorType];
    }
    return null;
  };

  const { currentPath, previousPath } = routes;
  const isAnotherPage = currentPath !== previousPath;
  const projectId = projectIdParam ?? null;

  if (sqlAutomations) {
    const sqlAutomationsState = state.flowCategories?.sqlAutomations ?? {};
    const pipelines = sqlAutomationsPipelinesSelector(state);
    return {
      isSQLAutomations: true,
      activeViews,
      accessControl,
      selectedFlow: state.hashedState.data?.flows?.selected,
      useExpanded: state.hashedState.data?.flows?.useExpanded,
      pipelines,
      filters: filters[SearchFilterViews.SQL_AUTOMATIONS] ?? [],
      rootNodeKeys: sqlAutomationsState.ids ?? [],
      flowNodes: entities.flowNodes,
      currentPage: isAnotherPage ? 1 : (sqlAutomationsState.meta?.currentPage ?? 1),
      sortCriteria,
      lastAddedSourceId,
      lastAddedDataSetId,
      lastAddedSinkId,
      getSourceMeta,
      entities,
      dataSetSchemas,
      lastLoaded: null,
      isLoadingPipelines:
        !!request[GET_FLOWS_SQL_AUTOMATIONS_REQUEST] &&
        (sqlAutomationsState.ids?.length ?? 0) === 0,
      isLoadingProjectById: false,
      loadingRemoveFromProject: false,
      loadingDeleteProject: false,
      isRefreshingPipeline: !!request[REFRESH_PIPELINE_REQUEST],
      selectedCardID: pipelinesSlice.detailView?.id ?? null,
      projectSelectedFlow: null,
      projectDataFlowMapping: {},
      lastAddedSource,
      projectById: {},
      flowOperationRequestFlags: {
        [CLONE_FLOW_REQUEST]: request[CLONE_FLOW_REQUEST],
        [DELETE_PIPELINE_REQUEST]: request[DELETE_PIPELINE_REQUEST],
        [REMOVE_FLOW_FROM_PROJECT_REQUEST]: request[REMOVE_FLOW_FROM_PROJECT_REQUEST],
      },
      metricsInterval: getFlowMetricsInterval(state),
    };
  }

  const pipelines = memoizedPipelinesSelector(state);
  const filteredPipelines = projectId
    ? pipelines.filter((pipeline) => String(pipeline.project_id) === String(projectId))
    : pipelines;

  return {
    activeViews,
    accessControl,
    selectedFlow: state.hashedState.data?.flows?.selected,
    useExpanded: state.hashedState.data?.flows?.useExpanded,
    pipelines: filteredPipelines,
    filters: filters[projectId ? SearchFilterViews.PROJECT_PIPELINES : SearchFilterViews.PIPELINES],
    rootNodeKeys: state.pipelines.rootNodeKeys,
    flowNodes: entities.flowNodes,
    /** Store page for API calls; `currentPage` is forced to 1 when routes change (see isAnotherPage). */
    listingPageForRequest: listingCurrentPage,
    currentPage: isAnotherPage ? 1 : listingCurrentPage,
    sortCriteria,
    lastAddedSourceId,
    lastAddedDataSetId,
    lastAddedSinkId,
    getSourceMeta,
    entities,
    dataSetSchemas,
    lastLoaded: pipelinesSlice.lastUpdated,
    isLoadingPipelines: !!request[GET_PIPELINES_REQUEST],
    isLoadingProjectById: !!request[GET_PROJECT_REQUEST],
    loadingRemoveFromProject: !!request[REMOVE_FLOW_FROM_PROJECT_REQUEST],
    loadingDeleteProject: !!request[DELETE_PROJECTS_REQUEST],
    isRefreshingPipeline: !!request[REFRESH_PIPELINE_REQUEST],
    selectedCardID: pipelinesSlice.detailView?.id ?? null,
    projectSelectedFlow,
    projectDataFlowMapping,
    lastAddedSource,
    projectById,
    flowOperationRequestFlags: {
      [CLONE_FLOW_REQUEST]: request[CLONE_FLOW_REQUEST],
      [DELETE_PIPELINE_REQUEST]: request[DELETE_PIPELINE_REQUEST],
      [REMOVE_FLOW_FROM_PROJECT_REQUEST]: request[REMOVE_FLOW_FROM_PROJECT_REQUEST],
    },
    metricsInterval: getFlowMetricsInterval(state),
  };
}

/**
 * Shared dispatch for Pipelines. When sqlAutomations is true, loadPipelines
 * delegates to loadSqlAutomationsPipelines and always reads sort from store.
 */
function createPipelinesMapDispatch(sqlAutomations) {
  const baseDispatch = {
    saveHashedState: saveState,
    refreshPipeline,
    resetPageNumbers,
    setCurrentPage,
    setSortCriteria,
    resetSortCriteria,
    updateSearchFilters,
    setContextualSupport,
    setDetailViewCard,
    unsetContextualSupport,
    setTotalPages,
    setTotalItems,
    getCredentialsGroups,
  };

  if (sqlAutomations) {
    return {
      ...baseDispatch,
      loadPipelines: (params) => (dispatch, getState) => {
        const state = getState();
        return dispatch(
          loadSqlAutomationsPipelines({
            page: params?.page,
            sort: state?.app?.listing?.sortCriteria,
            filters:
              state?.app?.visibilityFilters?.filters?.[SearchFilterViews.SQL_AUTOMATIONS] ?? [],
            availableFilters: state?.app?.visibilityFilters?.availableFilters,
          }),
        );
      },
      setCurrentProjectId: () => {},
      getProjectById: () => Promise.resolve(),
    };
  }

  return {
    ...baseDispatch,
    loadPipelines,
    setCurrentProjectId,
    getProjectById,
  };
}

const mapStateToProps = (state, ownProps) =>
  getPipelinesState(state, {
    projectId: ownProps.match?.params?.projectId,
  });

const mapStateToPropsSqlAutomations = (state) => getPipelinesState(state, { sqlAutomations: true });

function PipelinesWithMetricsAdapter(props) {
  const { requestFlowMetrics } = useMetricsAdapter();
  return <Pipelines {...props} requestFlowMetrics={requestFlowMetrics} />;
}

const PipelinesContainer = connect(
  mapStateToProps,
  createPipelinesMapDispatch(false),
)(withRouter(PipelinesWithMetricsAdapter));

const SqlAutomationsPipelinesContainer = connect(
  mapStateToPropsSqlAutomations,
  createPipelinesMapDispatch(true),
)(withRouter(PipelinesWithMetricsAdapter));

PipelinesContainer.screenConfig = {
  screenName: 'PipelinesContainer',
  pageTitle: 'All Data Flows',
  domain: 'integrate',
  modals: [ModalMoveToProject, ModalReingest, ModalFilePreview, ModalAddToMarketplace],
  path: [`${ROUTE_REDESIGN}${ROUTE_CONNECT}${ROUTE_PIPELINES}`],
  exact: true,
  defaultProps: {
    withHeader: false,
    disableProjectView: true,
  },
};

const PipelinesByProjectContainer = { ...PipelinesContainer };

PipelinesByProjectContainer.screenConfig = {
  screenName: 'PipelinesByProjectContainer',
  domain: 'integrate',
  exact: true,
  modals: [ModalFilePreview, ModalReingest, ModalMoveToProject, ModalAddToMarketplace],
  path: `${ROUTE_REDESIGN}${ROUTE_CONNECT}/projects/:projectId`,
  pageTitle: 'Project Detail',
  defaultProps: {
    withHeader: false,
    disableProjectView: true,
  },
};

export { PipelinesByProjectContainer, SqlAutomationsPipelinesContainer };
export default PipelinesContainer;
